import React, { useEffect, useMemo, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useAnchoredFlyout, getSidebarFlyoutLeft } from "./useAnchoredFlyout";
import { useTranslation } from "react-i18next";
import { Iconfont, BatteryLevel } from "@eweadn-qiankun/shared-components";
import {
  isProductPicMissing,
  requestProductPic,
  useProductColor,
  productColorInstanceKey,
} from "@eweadn-qiankun/shared-store";
import {
  getConnectionText,
  getConnectionTextByWiredState,
  getKeyboardConnectionText,
  resolveMouseWiredConnection,
  resolveRongyuanKeyboardConnectionMode,
  getSubAppPortalContainer,
  isBeiyingWirelessKeyboard,
  isVx02CmediaEarphone,
  isFreqCBatteryUnavailable,
  isSdcxBatteryUnavailable,
  isHuafendaMagneticAxisEquipment,
  readMergedEquipmentData,
  SUPPORTED_DEVICES,
  formatDeviceDisplayLabel,
  isDemoModeActive,
} from "@eweadn-qiankun/shared-utils";
import "./DeviceStatus.scss";

function clampPower(p?: { level?: number; charging?: boolean } | null) {
  if (p == null || typeof p.level !== "number") return null;
  return {
    level: Math.min(100, Math.max(0, p.level)),
    charging: !!p.charging,
  };
}

type EqCfg = {
  vendorId?: unknown;
  productId?: unknown;
  solutionRovider?: unknown;
  displayname?: unknown;
  is4G?: boolean;
  connectionType?: string;
  workMode?: string | number;
};

type ConnectRow = {
  stableId?: string;
  configuration?: EqCfg;
  deviceInfo?: {
    power?: { level?: number; charging?: boolean };
    battery?: { level?: number; charging?: boolean };
    workMode?: string | number;
    isWired?: boolean;
  };
};

function isMouseEquipment(vendorId: unknown, productId: unknown): boolean {
  if (vendorId == null || productId == null) return false;
  const vid =
    typeof vendorId === "string" ? parseInt(vendorId, 16) : Number(vendorId);
  const pid =
    typeof productId === "string" ? parseInt(productId, 16) : Number(productId);
  if (Number.isNaN(vid) || Number.isNaN(pid)) return false;
  return (
    SUPPORTED_DEVICES.mouse as { vendorId: number; productId?: number }[]
  ).some(
    (d) =>
      d.vendorId === vid &&
      (typeof d.productId !== "number" || d.productId === pid)
  );
}

/** 星闪（xingshan）方案：侧栏不展示电量（与首页设备卡片一致） */
function isXingshanSolution(eq: EqCfg | null | undefined): boolean {
  return String(eq?.solutionRovider ?? "") === "xingshan";
}

/** 匠盟（jiangmeng）方案 */
function isJiangmengSolution(eq: EqCfg | null | undefined): boolean {
  return String(eq?.solutionRovider ?? "") === "jiangmeng";
}

function cfgMatchesEquipment(eq: EqCfg | null, cfg: EqCfg | undefined) {
  if (!eq || !cfg) return false;
  if (Number(eq.vendorId) !== Number(cfg.vendorId)) return false;
  if (Number(eq.productId) !== Number(cfg.productId)) return false;
  const es = String(eq.solutionRovider ?? "");
  const cs = String(cfg.solutionRovider ?? "");
  if (es && cs && es !== cs) return false;
  const ed = String(eq.displayname ?? "");
  const cd = String(cfg.displayname ?? "");
  if (ed && cd && ed !== cd) return false;
  return true;
}

function pickBattery(
  ordered: Array<ReturnType<typeof clampPower> | null | undefined>
) {
  for (const p of ordered) {
    if (!p) continue;
    if (p.level > 0 || p.charging)
      return { level: p.level, charging: p.charging };
  }
  for (const p of ordered) {
    if (p) return { level: p.level, charging: p.charging };
  }
  return { level: 0, charging: false };
}

/**
 * 多鼠同连时，store 里可能仍残留上一台的 getState 推送。
 * 仅当 live.modelId / uuid 与当前 equipment 对得上才采信 live 电量/连接态。
 */
function liveMatchesCurrentEquipment(
  eq: EqCfg | null,
  mi:
    | {
        modelId?: string;
        uuid?: unknown;
        name?: string;
      }
    | null
    | undefined
): boolean {
  if (!eq || !mi) return false;
  const eqDeviceId = String((eq as { deviceId?: unknown }).deviceId ?? "")
    .trim()
    .toLowerCase();
  const modelId = String(mi.modelId ?? "")
    .trim()
    .toLowerCase();
  if (eqDeviceId && modelId) {
    return eqDeviceId === modelId;
  }
  const provider = String(eq.solutionRovider ?? "").toLowerCase();
  const uuid = String(mi.uuid ?? "");
  if (provider === "yxt") {
    return uuid.startsWith("yxt-");
  }
  if (provider === "jiangmeng") {
    return uuid.startsWith("mouse-") || (!!modelId && !uuid.startsWith("yxt-"));
  }
  const eqName = String(eq.displayname ?? "")
    .trim()
    .toLowerCase();
  const liveName = String(mi.name ?? "")
    .trim()
    .toLowerCase();
  if (eqName && liveName) {
    return eqName === liveName || liveName.includes(eqName);
  }
  // 无身份字段时不采信 live，避免串台
  return false;
}

/** 优先内存 connectList（首页实时），再回退旧版 localStorage 持久化 */
function readConnectListRows(): ConnectRow[] | null {
  if (typeof window === "undefined") return null;
  try {
    const live = (
      window as unknown as { __EWEADN_CONNECT_LIST__?: ConnectRow[] }
    ).__EWEADN_CONNECT_LIST__;
    if (Array.isArray(live) && live.length > 0) return live;
  } catch {
    /* ignore */
  }
  try {
    const list = JSON.parse(
      localStorage.getItem("app-hidGlobalStore") || "null"
    )?.state?.connectListStore as ConnectRow[] | undefined;
    return Array.isArray(list) ? list : null;
  } catch {
    return null;
  }
}

function findConnectRowForEquipment(eq: EqCfg | null): ConnectRow | undefined {
  if (!eq || eq.vendorId == null || eq.productId == null) return undefined;
  const list = readConnectListRows();
  if (!list?.length) return undefined;

  const isMouse = isMouseEquipment(eq.vendorId, eq.productId);
  if (isMouse) {
    try {
      const sid = localStorage.getItem("mouseCurrentStableId");
      if (sid) {
        const hit = list.find((x) => x?.stableId === sid);
        if (hit && cfgMatchesEquipment(eq, hit.configuration)) return hit;
      }
    } catch {
      /* ignore */
    }
    return list.find((x) => cfgMatchesEquipment(eq, x?.configuration));
  }
  return list.find((x) => cfgMatchesEquipment(eq, x?.configuration));
}

/** 与首页设备卡片同源：当前 equipment 对应 connectList 行的 `deviceInfo.power` */
function readListPower(eq: EqCfg | null): ReturnType<typeof clampPower> {
  const row = findConnectRowForEquipment(eq);
  return clampPower(row?.deviceInfo?.power ?? row?.deviceInfo?.battery);
}

/** 与 readListPower 同源：首页 connectList 上的 isWired（YXT 等进子应用前已写入） */
function readListIsWired(eq: EqCfg | null): boolean | undefined {
  const row = findConnectRowForEquipment(eq);
  const wired = row?.deviceInfo?.isWired;
  return typeof wired === "boolean" ? wired : undefined;
}

function readSessionJson(
  sessionKey: string,
  legacyKey: string
): Record<string, unknown> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      window.sessionStorage.getItem(sessionKey) ??
      window.localStorage.getItem(legacyKey);
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** 合并 equipmentData 与进入内页时的 deviceInitialStatus，供连接态与首页卡片一致 */
function buildKeyboardConnectionConfig(
  equipment: EqCfg | null,
  initialStatus: { workMode?: string | number } | null | undefined
): EqCfg | null {
  if (!equipment) return null;
  const initWm = initialStatus?.workMode;
  const hasInitWm =
    initWm !== undefined && initWm !== null && String(initWm) !== "";
  const cfg = hasInitWm
    ? ({ ...equipment, workMode: initWm } as EqCfg)
    : ({ ...equipment } as EqCfg);
  if (
    cfg.solutionRovider === "beiying" &&
    cfg.is4G !== true &&
    isBeiyingWirelessKeyboard(cfg)
  ) {
    return { ...cfg, is4G: true };
  }
  if (cfg.solutionRovider === "rongyuan") {
    const mode = resolveRongyuanKeyboardConnectionMode(cfg);
    if (mode === "wireless" && cfg.is4G !== true) {
      return { ...cfg, is4G: true };
    }
    if (mode === "bluetooth" && cfg.connectionType !== "bluetooth") {
      return { ...cfg, connectionType: "bluetooth" };
    }
  }
  if (isVx02CmediaEarphone(cfg)) {
    return { ...cfg, workMode: "2" };
  }
  return cfg;
}

/** 演示模式无 HID：按配置表 is4G / 进页 workMode 推断有线或 2.4G（与首页演示卡一致） */
function inferDemoMouseWired(
  eq: EqCfg | null | undefined,
  initialStatus: { workMode?: string | number } | null | undefined
): boolean {
  if (eq?.is4G === true) return false;
  const wm = initialStatus?.workMode;
  if (wm !== undefined && wm !== null && String(wm) !== "") {
    return String(wm) !== "2" && wm !== 2;
  }
  return true;
}

interface DeviceStatusProps {
  useSdkApi?: unknown;
  mouseDeviceInfo?: {
    name?: string;
    battery?: { level: number; charging: boolean };
    modelId?: string;
    info?: { type?: number };
    [key: string]: unknown;
  };
  /** 点击「设备列表」时的回调，不传则使用默认跳转首页 */
  onDeviceListClick?: () => void;
  /** 侧栏收起为窄条时：仅显示设备缩略图，悬停右侧展开完整卡片 */
  sidebarCollapsed?: boolean;
}

const DeviceStatus: React.FC<DeviceStatusProps> = ({
  useSdkApi: _useSdkApi,
  mouseDeviceInfo,
  onDeviceListClick,
  sidebarCollapsed = false,
}) => {
  const handleDeviceListClick = () => {
    if (onDeviceListClick) {
      onDeviceListClick();
    } else {
      window.location.href = "/";
    }
  };
  const { t } = useTranslation();

  // session 为进页快照；local 可能含华奋达 normalize 后的 VID/PID（演示模式 X87 V2 等）
  const equipmentData = readMergedEquipmentData() as EqCfg | null;
  const deviceInitialStatus = readSessionJson(
    "eweadn:session:deviceInitialStatus",
    "deviceInitialStatus"
  ) as {
    workMode?: string | number;
    power?: { level?: number; charging?: boolean };
    battery?: { level?: number; charging?: boolean };
    type?: number;
    isWired?: boolean;
  } | null;

  const savedPB = deviceInitialStatus?.power ?? deviceInitialStatus?.battery;

  const mi = mouseDeviceInfo as
    | {
        name?: string;
        battery?: { level?: number; charging?: boolean };
        power?: { level?: number; charging?: boolean };
        modelId?: string;
        info?: { type?: number };
        [key: string]: unknown;
      }
    | undefined;

  const isMouse = isMouseEquipment(
    equipmentData?.vendorId,
    equipmentData?.productId
  );
  const list = readListPower(equipmentData);
  const listIsWired = isMouse ? readListIsWired(equipmentData) : undefined;
  const liveTrusted = liveMatchesCurrentEquipment(equipmentData, mi);
  const live = isMouse ? clampPower(mi?.battery ?? mi?.power) : null;
  const initialClamped = clampPower(savedPB);
  // 多鼠同连：勿优先采信上一台残留的 live（否则侧栏会出现 S9 的 49%/2.4G）
  const battery = pickBattery(
    isMouse
      ? liveTrusted
        ? [live, list, initialClamped]
        : [list, initialClamped, live]
      : [list, initialClamped, null]
  );

  const name =
    mi?.name ??
    equipmentData?.displayname ??
    equipmentData?.productName ??
    "Unknown Device";
  const displayLabel = formatDeviceDisplayLabel(name);

  // 按 displayname 识别设备（避免 productName 导致 X87/X87 HE 混用）
  const displaynameForImage =
    equipmentData?.displayname ||
    mouseDeviceInfo?.modelId ||
    mouseDeviceInfo?.name;

  // 与首页设备卡片一致：按 VID/PID/uuid 实例键取图，多「X98」等同屏时与卡片配色一致
  const { getDeviceImage } = useProductColor();
  const productImageKey =
    equipmentData && typeof equipmentData === "object"
      ? productColorInstanceKey(equipmentData)
      : displaynameForImage || "";
  const deviceImage = productImageKey ? getDeviceImage(productImageKey) : "";

  useEffect(() => {
    const modelKey = (
      equipmentData?.displayname ||
      mouseDeviceInfo?.modelId ||
      mouseDeviceInfo?.name ||
      ""
    )
      .toString()
      .trim();
    if (modelKey) requestProductPic(modelKey);
  }, [
    equipmentData?.displayname,
    mouseDeviceInfo?.modelId,
    mouseDeviceInfo?.name,
  ]);

  // 连接状态图标
  const getConnectionIcon = (statusText: string) => {
    const isWireless =
      statusText?.includes(t("device.connection.wireless")) ||
      statusText?.includes(t("device.connection.bluetooth"));
    return isWireless ? "wireless" : "wired";
  };

  const mouseWorkMode =
    (mi as { workMode?: string | number } | undefined)?.workMode ??
    deviceInitialStatus?.workMode;

  const resolvedMouseWiredFromSources = useMemo((): boolean | undefined => {
    if (!isMouse) return undefined;
    const trustedLive = liveMatchesCurrentEquipment(equipmentData, mi);
    const wiredFromStore = trustedLive
      ? typeof mi?.isWired === "boolean"
        ? mi.isWired
        : typeof listIsWired === "boolean"
          ? listIsWired
          : typeof deviceInitialStatus?.isWired === "boolean"
            ? deviceInitialStatus.isWired
            : undefined
      : typeof listIsWired === "boolean"
        ? listIsWired
        : typeof deviceInitialStatus?.isWired === "boolean"
          ? deviceInitialStatus.isWired
          : undefined; // 不采信上一台 live.isWired，交给 configIs4G / workMode
    const workModeTrusted = trustedLive
      ? mouseWorkMode
      : (deviceInitialStatus?.workMode ??
        (typeof listIsWired === "boolean"
          ? listIsWired
            ? "0"
            : "2"
          : mouseWorkMode));
    return resolveMouseWiredConnection({
      solutionRovider: equipmentData?.solutionRovider,
      isWired: wiredFromStore,
      type: trustedLive
        ? (mouseDeviceInfo?.info?.type ?? deviceInitialStatus?.type)
        : (deviceInitialStatus?.type ?? mouseDeviceInfo?.info?.type),
      workMode: workModeTrusted,
      configIs4G:
        typeof equipmentData?.is4G === "boolean"
          ? equipmentData.is4G
          : undefined,
      configWorkMode: equipmentData?.workMode,
    });
  }, [
    equipmentData,
    isMouse,
    listIsWired,
    mi,
    mouseDeviceInfo?.info?.type,
    deviceInitialStatus,
    mouseWorkMode,
  ]);

  // 获取连接状态文本（与首页 WorkModeAndPower 同源逻辑）
  const connectionText = useMemo(() => {
    if (isMouse) {
      if (typeof resolvedMouseWiredFromSources === "boolean") {
        return getConnectionTextByWiredState(resolvedMouseWiredFromSources, t);
      }
      if (isJiangmengSolution(equipmentData)) {
        const type = mouseDeviceInfo?.info?.type ?? deviceInitialStatus?.type;
        return getConnectionText(type, t);
      }
      return t("device.connection.detectingDevice");
    }

    const cfg = buildKeyboardConnectionConfig(
      equipmentData,
      deviceInitialStatus
    );
    return getKeyboardConnectionText(cfg, t);
  }, [
    equipmentData,
    mouseDeviceInfo,
    deviceInitialStatus,
    isMouse,
    resolvedMouseWiredFromSources,
    t,
  ]);

  // 鼠标 / 键盘连接态分开判断，互不借用字段
  const demoMouseWired =
    isDemoModeActive() &&
    isMouse &&
    typeof resolvedMouseWiredFromSources !== "boolean" &&
    !isJiangmengSolution(equipmentData)
      ? inferDemoMouseWired(equipmentData, deviceInitialStatus)
      : undefined;
  const resolvedMouseWired =
    typeof resolvedMouseWiredFromSources === "boolean"
      ? resolvedMouseWiredFromSources
      : demoMouseWired;

  const deviceIsWired = isMouse
    ? typeof resolvedMouseWired === "boolean"
      ? resolvedMouseWired
      : isJiangmengSolution(equipmentData)
        ? getConnectionIcon(connectionText) === "wired"
        : false
    : getConnectionIcon(connectionText) === "wired";
  const displayConnectionText = isMouse
    ? typeof resolvedMouseWired !== "boolean" &&
      !isJiangmengSolution(equipmentData)
      ? t("device.connection.detectingDevice")
      : deviceIsWired
        ? t("device.status.connectionWired")
        : t("device.status.connectionWireless")
    : connectionText;

  // 匠盟有线：显示电量图标，不显示百分比数字
  const hideJiangmengWiredBatteryPercentage =
    isMouse && isJiangmengSolution(equipmentData) && deviceIsWired;
  // 贝盈鼠标充电中：显示「充电中」，不显示百分比（与匠盟一致）
  const hideBeiyingMouseChargingPercentage =
    isMouse &&
    String(equipmentData?.solutionRovider ?? "") === "beiying" &&
    battery.charging;
  // 达克有线充电中：百分比字节常为 0，显示「充电中」避免误显 0%
  const hideDakaiWiredChargingPercentage =
    !isMouse &&
    String(equipmentData?.solutionRovider ?? "") === "dakai" &&
    deviceIsWired &&
    battery.charging;

  const showBattery =
    !isXingshanSolution(equipmentData) &&
    !isFreqCBatteryUnavailable(equipmentData, mi) &&
    !isSdcxBatteryUnavailable(equipmentData) &&
    !isHuafendaMagneticAxisEquipment(equipmentData) &&
    !!(list || live || savedPB);

  // 如果没有设备信息，不显示或显示占位符
  const placeholder = !mouseDeviceInfo && !equipmentData;

  const cardMain = placeholder ? (
    <div className="device-status-placeholder">{t("no_device")}</div>
  ) : (
    <div className="device-status-card">
      <div className="devicestatus-image-container">
        {isProductPicMissing(deviceImage) ? (
          <span className="devicestatus-image-empty">暂未上传产品图</span>
        ) : (
          <img
            src={deviceImage}
            alt={displayLabel}
            className="devicestatus-image"
          />
        )}
      </div>

      <div className="devicestatus-name" title={displayLabel}>
        {t("common.brandName")} {displayLabel}
      </div>

      <div className="devicestatus-meta">
        {showBattery && (
          <div className="battery-status">
            <BatteryLevel
              level={battery.level}
              charging={battery.charging}
              showPercentage={
                !hideJiangmengWiredBatteryPercentage &&
                !hideBeiyingMouseChargingPercentage &&
                !hideDakaiWiredChargingPercentage
              }
              showIcon={true}
            />
          </div>
        )}

        <div className="connection-status">
          <Iconfont
            icon={deviceIsWired ? "wired" : "wireless"}
            className="status-icon"
          />
          <span className="connection-text">{displayConnectionText}</span>
        </div>
      </div>
    </div>
  );

  const overlayBtn = (
    <div className="device-status-overlay">
      <button
        type="button"
        className="device-list-btn"
        onClick={handleDeviceListClick}
      >
        <span className="device-status-devicelist">
          {t("device.status.deviceList")}
        </span>
        <Iconfont icon="chevronright" className="status-icon" />
      </button>
    </div>
  );

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const anchorRef = useCallback((node: HTMLDivElement | null) => {
    setAnchorEl(node);
  }, []);
  const getAnchor = useCallback(() => anchorEl, [anchorEl]);
  const {
    visible: hoverPanelVisible,
    pos: hoverPanelPos,
    portalRef: hoverPanelRef,
    portalHoverHandlers,
  } = useAnchoredFlyout({
    getAnchor,
    fixedLeft: getSidebarFlyoutLeft(sidebarCollapsed),
    active: sidebarCollapsed,
    hideDelay: 300,
  });

  const hoverPanel = (
    <div
      ref={hoverPanelRef}
      className={`device-status-hover-panel device-status-hover-panel--portal${hoverPanelVisible ? " device-status-hover-panel--portal-open" : ""}`}
      style={{
        left: hoverPanelPos.left,
        top: hoverPanelPos.top,
        visibility: hoverPanelVisible ? "visible" : "hidden",
        pointerEvents: hoverPanelVisible ? "auto" : "none",
      }}
      {...portalHoverHandlers}
    >
      <span className="device-status-hover-panel__bridge" aria-hidden />
      {deviceImage ? (
        <div
          className="device-status-bg-image"
          style={{ backgroundImage: `url(${deviceImage})` }}
          aria-hidden
        />
      ) : null}
      <div className="device-status-inner device-status-inner--popover">
        {cardMain}
      </div>
      {!placeholder ? overlayBtn : null}
    </div>
  );

  if (sidebarCollapsed) {
    return (
      <div className="device-status device-status--sidebar-collapsed">
        <div className="device-status-pop-anchor" ref={anchorRef}>
          <button
            type="button"
            className="device-status-collapsed-thumb"
            onClick={handleDeviceListClick}
            aria-label={placeholder ? t("no_device") : name}
          >
            {placeholder ? (
              <span className="device-status-collapsed-empty">—</span>
            ) : (
              <Iconfont
                icon={isMouse ? "mouse" : "device"}
                className="device-status-collapsed-type-icon"
              />
            )}
          </button>
        </div>
        {typeof document !== "undefined" &&
          createPortal(hoverPanel, getSubAppPortalContainer())}
      </div>
    );
  }

  return (
    <div className="device-status">
      {deviceImage && (
        <div
          className="device-status-bg-image"
          style={{ backgroundImage: `url(${deviceImage})` }}
          aria-hidden
        />
      )}
      <div className="device-status-inner">{cardMain}</div>
      {overlayBtn}
    </div>
  );
};

export default DeviceStatus;
